
import { Insurance, NonPartnerInsurance } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import {
  useNonPartnerInsuranceByStudentIdQuery, useNonPartnerInsuranceCompanyQuery, useUserInsurancesQuery
} from '@uplift/queries';
import { AddressVerificationProvider } from '@uplift/ui-v2';

import { backendBirthDateFormatToFormFormat } from '../../../utils';
import { InsuranceInfoNextData } from '../../types';
import { InsuranceInfoContent } from './insurance-info-content';
import { InsuranceInfoFormValues } from './validation';

export type InsuranceInfoProps = {
  onBack: () => void;
  onNext: (data: InsuranceInfoNextData) => void;
  isInviteOrDirectFlow: boolean;
  providerId: string;
  stateId: string;
  insuranceId?: string;
  banner?: React.ReactNode;
  isBookingSponsored?: boolean;
  isPartnerInsurance?: boolean;
};


const getInitialValues = (
  selectedInsuranceId: string,
  insurance?: Insurance | NonPartnerInsurance,
  isPartnerInsurance = true,
  companyName?: string
): InsuranceInfoFormValues => {
  if (!insurance) {
    return getEmptyInitialValues(selectedInsuranceId);
  }

  if (isPartnerInsurance) {
    return getPartnerInsuranceValues(selectedInsuranceId, insurance as Insurance);
  }

  return getNonPartnerInsuranceValues(selectedInsuranceId, insurance as NonPartnerInsurance, companyName);
};

const getEmptyInitialValues = (selectedInsuranceId: string): InsuranceInfoFormValues => ({
  isPrimarySubscriber: true,
  insuranceProvider: selectedInsuranceId,
  memberId: '',
  groupNumber: '',
  insuranceCardFront: null,
  insuranceCardBack: null,
  primarySubscriberFirstName: '',
  primarySubscriberLastName: '',
  primarySubscriberDateOfBirth: '',
  address: {
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  },
});

const getPartnerInsuranceValues = (
  selectedInsuranceId: string,
  insurance: Insurance
): InsuranceInfoFormValues => {
  const isPrimary = insurance?.isPrimary ?? true;

  const data: InsuranceInfoFormValues = {
    isPrimarySubscriber: isPrimary,
    insuranceProvider: selectedInsuranceId ?? insurance?.companyId,
    memberId: insurance?.subscriberId ?? '',
    groupNumber: insurance?.groupNumber ?? '',
    insuranceCardFront: insurance?.cardFrontAssetId ?? null,
    insuranceCardBack: insurance?.cardBackAssetId ?? null,
    primarySubscriberFirstName: getPrimarySubscriberValue(isPrimary, insurance?.firstName ?? ''),
    primarySubscriberLastName: getPrimarySubscriberValue(isPrimary, insurance?.lastName ?? ''),
    primarySubscriberDateOfBirth: getPrimarySubscriberValue(
      isPrimary,
      insurance?.dob ? backendBirthDateFormatToFormFormat(insurance.dob) : ''
    ),
    address: {
      address1: getPrimarySubscriberValue(isPrimary, insurance?.address?.line1 ?? ''),
      address2: getPrimarySubscriberValue(isPrimary, insurance?.address?.line2 ?? ''),
      city: getPrimarySubscriberValue(isPrimary, insurance?.address?.city ?? ''),
      state: getPrimarySubscriberValue(isPrimary, insurance?.address?.stateId ?? ''),
      zip: getPrimarySubscriberValue(isPrimary, insurance?.address?.zip ?? ''),
    },
  };

  return data;
};

const getNonPartnerInsuranceValues = (
  selectedInsuranceId: string,
  insurance: NonPartnerInsurance,
  companyName: string
): InsuranceInfoFormValues => {
  const isPrimary = insurance.holderType === 'primary';

  return {
    isPrimarySubscriber: isPrimary,
    insuranceProvider: selectedInsuranceId ?? insurance.companyId,
    insuranceProviderFreeText: companyName ?? '',
    memberId: insurance.subscriberId ?? '',
    groupNumber: insurance.groupNumber ?? '',
    insuranceCardFront: insurance.cardPhotos.frontAssetId ?? null,
    insuranceCardBack: insurance.cardPhotos.backAssetId ?? null,
    primarySubscriberFirstName: getPrimarySubscriberValue(isPrimary, insurance.holderInfo.firstName),
    primarySubscriberLastName: getPrimarySubscriberValue(isPrimary, insurance.holderInfo.lastName),
    primarySubscriberDateOfBirth: getPrimarySubscriberValue(
      isPrimary,
      backendBirthDateFormatToFormFormat(insurance.holderInfo.dob)
    ),
    address: {
      address1: getPrimarySubscriberValue(isPrimary, insurance.holderInfo.address.line1),
      address2: getPrimarySubscriberValue(isPrimary, insurance.holderInfo.address.line2),
      city: getPrimarySubscriberValue(isPrimary, insurance.holderInfo.address.city),
      state: getPrimarySubscriberValue(isPrimary, insurance.holderInfo.address.stateId),
      zip: getPrimarySubscriberValue(isPrimary, insurance.holderInfo.address.zip),
    },
  };
};

const getPrimarySubscriberValue = (isPrimary: boolean, value: string) => isPrimary ? '' : value;

export const InsuranceInfo: React.VFC<InsuranceInfoProps> = props => {
  const currentUser = useCurrentuser();
  const { data: userInsurances = [], isInitialLoading: partnerInsuranceLoading } = useUserInsurancesQuery(currentUser.id);
  const { data: nonPartnerInsurance = [], isInitialLoading: nonPartnerInsuranceLoading } = useNonPartnerInsuranceByStudentIdQuery(currentUser.id, false, { enabled: !props.isPartnerInsurance });
  const lastNonPartnerInsurance = nonPartnerInsurance?.[nonPartnerInsurance.length - 1];
  const companyName = useNonPartnerInsuranceCompanyQuery(lastNonPartnerInsurance?.companyId, { enabled: !!lastNonPartnerInsurance }).data?.name;

  if (partnerInsuranceLoading || nonPartnerInsuranceLoading) {
    return null;
  }

  const userInsurance = userInsurances[0]?.insurance;
  const initialValues = props.isPartnerInsurance
    ? getInitialValues(props.insuranceId, userInsurances[0]?.insurance, true)
    : getInitialValues(props.insuranceId, lastNonPartnerInsurance, false, companyName);

  return (
    <AddressVerificationProvider
      shouldReverifyAddress={false}
      parsedVerifiedAddress={initialValues.address.address1 ? initialValues.address : null}
    >
      <InsuranceInfoContent
        {...props}
        isPartnerInsurance={props.isPartnerInsurance}
        initialValues={initialValues}
        lastNonPartnerInsurance={lastNonPartnerInsurance}
        partnerInsurance={userInsurance}
      />
    </AddressVerificationProvider>
  );
};
